/**
 * contentShared.js — helpers shared by CalendarBuilder + MockupStudio.
 * Role gating mirrors TasksPage (creators/reviewers = admin/Content Manager/
 * Scheduler; designers = Graphic Designer/Video Editor).
 */

import { logoForClientName } from '../../utils/brandLogos';

export const OBJECTIVES = ['Product Promotion', 'Information', 'Trending', 'Deal Promotion', 'Engagement', 'Awareness', 'Other'];
export const POST_TYPES = ['Reel', 'Carousel', 'Post', 'Story'];
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const CREATOR_ROLES  = ['Content Manager', 'Scheduler'];
const DESIGNER_ROLES = ['Graphic Designer', 'Video Editor'];

// Post numbers follow the posting order, not the grid (owner, 2026-10-12): the grid is
// laid out like Instagram — the newest post sits top-left — so the LAST post in the grid
// goes up first and is #1, and the top-left post has the highest number.
export function postNumber(index, total) { return total - index; }

export function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
export function nowIso() { return new Date().toISOString(); }
export function today() { return new Date().toISOString().slice(0, 10); }
export function thisMonth() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
export function nextMonth(ym) { if (!ym) return thisMonth(); const [y, m] = ym.split('-').map(Number); const d = new Date(y, m, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
export function monthLabel(ym) { if (!ym) return ''; const [y, m] = ym.split('-'); return `${MONTHS[parseInt(m, 10) - 1]} ${y}`; }
export function fmtDate(d) { if (!d) return '—'; try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } }

export function empRoles(emp) {
  if (!emp) return [];
  return Array.isArray(emp.roles) && emp.roles.length ? emp.roles : [emp.role].filter(Boolean);
}
export function isCreator(session, employees) {
  if (!session) return false;
  if (session.role === 'admin') return true;
  const emp = (employees || []).find(e => e.id === session.empId);
  return empRoles(emp).some(r => CREATOR_ROLES.includes(r));
}
export const isReviewer = isCreator; // same set reviews posts
// Who can build/edit mockups: the creators PLUS graphic designers.
const MOCKUP_EDITOR_ROLES = ['Content Manager', 'Scheduler', 'Graphic Designer'];
export function canMakeMockups(session, employees) {
  if (!session) return false;
  if (session.role === 'admin') return true;
  const emp = (employees || []).find(e => e.id === session.empId);
  return empRoles(emp).some(r => MOCKUP_EDITOR_ROLES.includes(r));
}
export function isDesigner(session, employees) {
  if (!session || session.role === 'admin') return false;
  const emp = (employees || []).find(e => e.id === session.empId);
  return empRoles(emp).some(r => DESIGNER_ROLES.includes(r));
}
export function designerEmployees(employees) {
  return (employees || []).filter(e => empRoles(e).some(r => DESIGNER_ROLES.includes(r)) && e.active !== false);
}
// Recipients who should be pinged when a designer submits — admin + content managers/schedulers.
export function reviewerRecipients(employees) {
  const ids = ['admin'];
  (employees || []).forEach(e => {
    if (e.active === false) return;
    if (empRoles(e).some(r => CREATOR_ROLES.includes(r))) ids.push(e.id);
  });
  return ids;
}
export function empName(employees, id) {
  if (id === 'admin') return 'Admin';
  const e = (employees || []).find(x => x.id === id);
  return e ? e.name : id;
}

// All posts in a calendar, whether stored as grid-rows (new) or a flat list (legacy).
export function calendarPosts(cal) {
  if (Array.isArray(cal?.data?.grids)) return cal.data.grids.flatMap(g => g.posts || []);
  return cal?.data?.rows || [];
}

// Flatten clients + their sub-accounts into { id, name } options.
export function clientOptions(clients) {
  const out = [];
  (clients || []).forEach(c => {
    if (c.formerClient) return;
    out.push({ id: c.id, name: c.name });
    (c.subAccounts || []).forEach(s => out.push({ id: s.id, name: `${c.name} — ${s.name}`, plainName: s.name }));
  });
  return out;
}

// ── Mockup seeding (shared by MockupStudio + calendar auto-create) ──
// One source of truth for turning an approved calendar into a fresh mockup
// draft, so the "Build mockup" button and the on-approval auto-create can't drift.
export function slugHandle(name) { return (name || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
export function emptyMockupCell() {
  return { id: uid(), type: 'Post', coverPath: '', coverSource: 'designed', reelUrl: '', driveUrl: '', carouselPaths: [], caption: '', likes: '50 likes', date: '' };
}
export function mockupFromCalendar(cal, clients, createdBy) {
  const client = (clients || []).find(c => c.id === cal.clientId);
  const name = cal.clientName || client?.name || '';
  // Carry the designer's asset link (Drive/Canva) from the calendar post so the
  // client can open the high-res master from the mockup review page.
  const cells = calendarPosts(cal).map(r => ({ ...emptyMockupCell(), type: r.type || 'Post', caption: r.caption || '', date: r.date || '', driveUrl: r.fileLink || '' }));
  return {
    id: uid(), clientId: cal.clientId, clientName: name, monthYear: cal.monthYear,
    calendarId: cal.id, status: 'draft', createdAt: nowIso(), updatedAt: nowIso(), createdBy,
    data: {
      title: `${monthLabel(cal.monthYear)} Mockup`, handle: slugHandle(name),
      logoSlug: logoForClientName(name) || '', logoVariant: 'white',
      shareId: '', shareUrl: '', publishedAt: '',
      cells: cells.length ? cells : [emptyMockupCell()],
    },
  };
}

// Build a notification object (stored in the app_config `notifications` blob — safe).
export function makeNotif(empId, message, { id, name, client } = {}) {
  return { id: uid(), empId, message, taskId: id || '', taskName: name || '', client: client || '', kind: 'content', createdAt: nowIso(), read: false };
}
